import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {delay, filter, map, Observable, of, repeat, switchMap, take, throwError, timeout} from 'rxjs';
import {DeviceDetectorService} from 'ngx-device-detector';
import {
  ApiMode,
  CallbackPayload,
  buildSupportRequestZendeskTicket,
  ConfigService,
  ConnectMode,
  ConnectPayload,
  CspType,
  FormSupportRequest,
  Organization,
  OrganizationConnection,
  RecordLocatorResponse,
  LighthouseSourceSearch,
  LighthouseSourceSearchResult,
  ResponseWrapper,
  SDKMode,
  SearchFilter,
  ShlinkManifestCreateRequest,
  ShlinkManifestResponse,
  SystemConfig,
  VaultEhiExportRegisterRequest,
  VaultEhiExportResponse,
  VaultConnectionOrgAuthorizeResponse,
  waitForPostMessageOrgConnectionOrTimeout,
  waitForWebsocketOrgConnectionOrTimeout
} from '@shared-library';
import {NGXLogger} from 'ngx-logger';
import {v4 as uuidv4} from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class FastenService {

  constructor(
    private _httpClient: HttpClient,
    private deviceService: DeviceDetectorService,
    public configService: ConfigService,
    private logger: NGXLogger,
  ) {
    this.configService.systemConfigSubject.subscribe((systemConfig: SystemConfig) => {
      if (systemConfig.publicId && !systemConfig.org) {
        this.getOrgByPublicId(systemConfig.publicId).subscribe((org: Organization) => {
          this.configService.systemConfig = {org};
        });
      }
    });
  }

  public verificationWithWebsocket(cspType: CspType): Observable<CallbackPayload> {
    const roomId = uuidv4();
    const websocketUrl = this.generateWebsocketURL(roomId);

    const redirectUrl = new URL(`${environment.connect_api_endpoint_base}/bridge/identity_verification/connect`);
    redirectUrl.searchParams.set('public_id', this.configService.systemConfig$.publicId);
    redirectUrl.searchParams.set('csp_type', cspType || CspType.ClearCsp);
    redirectUrl.searchParams.set('connect_mode', ConnectMode.Websocket);
    redirectUrl.searchParams.set('room_id', roomId);

    this.logger.debug(redirectUrl.toString());
    const openedWindow = this.openWindowInPopup(redirectUrl);

    return this.waitForWebsocketNotification(websocketUrl, openedWindow!)
      .pipe(delay(2500));
  }

  public verificationWithPopup(cspType: CspType): Observable<CallbackPayload> {
    const redirectUrl = new URL(`${environment.connect_api_endpoint_base}/bridge/identity_verification/connect`);
    redirectUrl.searchParams.set('public_id', this.configService.systemConfig$.publicId);
    redirectUrl.searchParams.set('csp_type', cspType || CspType.ClearCsp);
    redirectUrl.searchParams.set('connect_mode', ConnectMode.Popup);

    const openedWindow = this.openWindowInPopup(redirectUrl);
    return this.waitForPopupNotification(openedWindow!);
  }

  public recordLocatorRegisterAndPollForStatus(): Observable<ResponseWrapper> {
    return this._httpClient.get<ResponseWrapper>(`${environment.connect_api_endpoint_base}/bridge/record_locator`, {
      params: {'public_id': this.configService.systemConfig$.publicId}
    }).pipe(
      switchMap((registerResponse: ResponseWrapper) => {
        if (registerResponse.data.status === 'failed') {
          return throwError(() => new Error('Record locator registration failed'));
        }
        if (registerResponse.data.status === 'success') {
          return of(registerResponse);
        }

        return this._httpClient.get<ResponseWrapper>(
          `${environment.connect_api_endpoint_base}/bridge/record_locator/${registerResponse.data.task_id}`,
          {params: {'public_id': this.configService.systemConfig$.publicId}}
        ).pipe(
          repeat({delay: 3000}),
          filter((statusResponse: ResponseWrapper) => statusResponse.data.status === 'success' || statusResponse.data.status === 'failed'),
          take(1),
          timeout(300000),
        );
      })
    );
  }

  public recordLocatorResults(taskId: string): Observable<RecordLocatorResponse> {
    return this._httpClient.get<ResponseWrapper>(
      `${environment.connect_api_endpoint_base}/bridge/record_locator/${taskId}/result`,
      {params: {'public_id': this.configService.systemConfig$.publicId}}
    ).pipe(
      map((response: ResponseWrapper) => {
        const rlsResponse = response.data as RecordLocatorResponse;
        if (!rlsResponse.discovered_patient_accounts) {
          rlsResponse.discovered_patient_accounts = {};
        }
        if (!rlsResponse.pending_patient_accounts) {
          rlsResponse.pending_patient_accounts = {};
        }
        if (!rlsResponse.connected_patient_accounts) {
          rlsResponse.connected_patient_accounts = {};
        }
        return rlsResponse;
      })
    );
  }

  public searchCatalogBrands(apiMode: ApiMode, filter: SearchFilter): Observable<LighthouseSourceSearch> {
    if((typeof filter.searchAfter === 'string' || filter.searchAfter instanceof String) && (filter.searchAfter as string).length > 0){
      filter.searchAfter = (filter.searchAfter as string).split(',')
    } else {
      filter.searchAfter = []
    }
    const endpointUrl = new URL(`${environment.connect_api_endpoint_base}/bridge/catalog/search`);

    return this._httpClient.post<ResponseWrapper>(endpointUrl.toString(), filter, {params: {
        'public_id': this.configService.systemConfig$.publicId,
        'api_mode': apiMode
      }})
      .pipe(
        map((response: ResponseWrapper) => {
          this.logger.info("Metadata RESPONSE", response)
          return response.data as LighthouseSourceSearch
        })
      );
  }

  public searchCatalogBrand(apiMode: ApiMode, brandId: string): Observable<LighthouseSourceSearchResult> {
    const endpointUrl = new URL(`${environment.connect_api_endpoint_base}/bridge/catalog/search/${brandId}`);

    return this._httpClient.post<ResponseWrapper>(endpointUrl.toString(), {}, {params: {
        'public_id': this.configService.systemConfig$.publicId,
        'api_mode': apiMode
      }})
      .pipe(
        map((response: ResponseWrapper) => {
          this.logger.info('Metadata RESPONSE', response);
          return response.data as LighthouseSourceSearchResult;
        })
      );
  }

  public getOrgByPublicId(publicId: string): Observable<Organization> {
    return this._httpClient.get<ResponseWrapper>(`${environment.connect_api_endpoint_base}/bridge/org`, {
      params: {'public_id': publicId}
    }).pipe(
      map((response: ResponseWrapper) => {
        this.logger.info('Organization', response);
        return response.data as Organization;
      })
    );
  }

  public getOrgConnectionById(publicId: string, orgConnectionId: string): Observable<OrganizationConnection> {
    return this._httpClient.get<ResponseWrapper>(
      `${environment.connect_api_endpoint_base}/bridge/org_connection/${orgConnectionId}`,
      {params: {'public_id': publicId}}
    ).pipe(
      map((response: ResponseWrapper) => {
        this.logger.info('Organization Connection Data', response);
        return response.data as OrganizationConnection;
      })
    );
  }

  public accountConnectWithPopup(connectData: ConnectPayload): Observable<CallbackPayload> {
    const redirectUrlParts = this.generateConnectURL(connectData);
    redirectUrlParts.searchParams.set('connect_mode', ConnectMode.Popup);
    this.logger.debug(redirectUrlParts.toString());

    const openedWindow = this.openWindowInPopup(redirectUrlParts);
    return this.waitForPopupNotification(openedWindow!, SDKMode.None);
  }

  public authorizeTefcaDirect(vaultConnectionIds: string[], externalId?: string): Observable<VaultConnectionOrgAuthorizeResponse> {
    const url = `${environment.connect_api_endpoint_base}/bridge/vault_connection/authorize`;
    return this._httpClient.post<ResponseWrapper>(url, {
      vault_connection_ids: vaultConnectionIds,
      external_id: externalId,
    }, {
      params: {public_id: this.configService.systemConfig$.publicId}
    }).pipe(map((resp: ResponseWrapper) => resp.data as VaultConnectionOrgAuthorizeResponse));
  }

  public revokeVaultProfileConnections(vaultProfileConnectionIds?: string[]): Observable<any> {
    const url = `${environment.connect_api_endpoint_base}/bridge/vault_connection/revoke`;
    return this._httpClient.post<ResponseWrapper>(url, {
      vault_connection_ids: vaultProfileConnectionIds,
    }, {
      params: {public_id: this.configService.systemConfig$.publicId}
    }).pipe(map((resp: ResponseWrapper) => resp.data));
  }

  public requestSupport(request: FormSupportRequest): Observable<any> {
    const endpointUrl = new URL(`${environment.connect_api_endpoint_base}/support/request`);
    const zendeskTicket = buildSupportRequestZendeskTicket(request, {
      organizationId: this.configService.systemConfig$.org?.id || '',
      organizationName: this.configService.systemConfig$.org?.name || '',
      apiMode: this.configService.systemConfig$.apiMode || 'test',
    });

    return this._httpClient.post<any>(endpointUrl.toString(), zendeskTicket, {
      params: {public_id: this.configService.systemConfig$.publicId}
    }).pipe(
      map((response: ResponseWrapper) => {
        // @ts-ignore
        return {}
      })
    );
  }

  public registerVaultEhiExport(
    payload: VaultEhiExportRegisterRequest,
    recaptchaResponseToken: string,
  ): Observable<VaultEhiExportResponse> {
    const url = `${environment.connect_api_endpoint_base}/bridge/vault/ehi-export`;
    return this._httpClient.post<ResponseWrapper>(url, payload, {
      params: {
        public_id: this.configService.systemConfig$.publicId,
        recaptcha_response_token: recaptchaResponseToken,
      },
    }).pipe(map((resp: ResponseWrapper) => resp.data as VaultEhiExportResponse));
  }

  public getVaultEhiExport(taskId: string): Observable<VaultEhiExportResponse> {
    const url = `${environment.connect_api_endpoint_base}/bridge/vault/ehi-export/${taskId}`;
    return this._httpClient.get<ResponseWrapper>(url, {
      params: {
        public_id: this.configService.systemConfig$.publicId,
      },
    }).pipe(map((resp: ResponseWrapper) => resp.data as VaultEhiExportResponse));
  }

  public getVaultProfileConnectionInfo(vaultProfileConnectionId: string): Observable<any> {
    const url = `${environment.connect_api_endpoint_base}/bridge/vault_connection/info/${vaultProfileConnectionId}`;
    return this._httpClient.get<ResponseWrapper>(url, {
      params: {
        public_id: this.configService.systemConfig$.publicId,
      },
    }).pipe(map((resp: ResponseWrapper) => resp.data ));
  }

  public createShlinkManifest(payload: ShlinkManifestCreateRequest): Observable<ShlinkManifestResponse> {
    const url = `${environment.connect_api_endpoint_base}/shlink/manifest/create`;
    return this._httpClient.post<ResponseWrapper>(url, payload, {
      params: {
        public_id: this.configService.systemConfig$.publicId,
      },
    }).pipe(map((resp: ResponseWrapper) => resp.data as ShlinkManifestResponse));
  }

  public openWindowInPopup(redirectUrlParts: URL): Window | null {
    const isDesktop = this.deviceService.isDesktop();
    let features = '';
    if (isDesktop) {
      features = 'popup=true,width=700,height=600';
    }
    return window.open(redirectUrlParts.toString(), '_blank', features);
  }

  public generateConnectURL(connectData: ConnectPayload): URL {
    const redirectUrlParts = new URL(`${environment.connect_api_endpoint_base}/bridge/connect`);
    const redirectParams = new URLSearchParams();
    redirectParams.set('public_id', this.configService.systemConfig$.publicId);
    redirectParams.set('brand_id', connectData.brand_id);
    redirectParams.set('portal_id', connectData.portal_id);
    redirectParams.set('endpoint_id', connectData.endpoint_id);

    if (connectData.org_connection_id) {
      redirectParams.set('reconnect_org_connection_id', connectData.org_connection_id);
    }
    if (connectData.external_id) {
      redirectParams.set('external_id', connectData.external_id);
    }
    if (connectData.external_state) {
      redirectParams.set('external_state', connectData.external_state);
    }
    if (connectData.vault_profile_connection_id) {
      redirectParams.set('reconnect_vault_profile_connection_id', connectData.vault_profile_connection_id);
    }

    redirectUrlParts.search = redirectParams.toString();
    return redirectUrlParts;
  }

  public generateWebsocketURL(roomId: string): URL {
    const websocketUrlParts = new URL(`wss://websocket.${environment.connect_base_domain}/v1`);
    const websocketParams = new URLSearchParams();
    websocketParams.set('public_id', this.configService.systemConfig$.publicId);
    websocketParams.set('room_id', roomId);
    websocketUrlParts.search = websocketParams.toString();
    return websocketUrlParts;
  }

  protected waitForPopupNotification(openedWindow: Window, overrideSdkMode?: SDKMode): Observable<CallbackPayload> {
    const sdkMode = overrideSdkMode ?? this.configService.systemConfig$.sdkMode;
    return waitForPostMessageOrgConnectionOrTimeout(this.logger, openedWindow, sdkMode);
  }

  protected waitForWebsocketNotification(websocketUrl: URL, openedWindow: Window, overrideSdkMode?: SDKMode): Observable<CallbackPayload> {
    const sdkMode = overrideSdkMode ?? this.configService.systemConfig$.sdkMode;
    return waitForWebsocketOrgConnectionOrTimeout(this.logger, websocketUrl, openedWindow, sdkMode);
  }
}
